import { deviceTypes, deviceHostings, deviceExecutions } from '../device-classification';
import { SearchSelect } from '../SearchSelect';
import { DeviceRegistrations } from '../DeviceLifecycle';
import type { DeviceDto, DeviceListDto } from '@onig-control/contracts';
import { Plus, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import {
  Drawer,
  Empty,
  ErrorMessage,
  Loading,
  PageHeader,
  Pagination,
} from '../components';
import { CompanyPicker, DeviceForm } from '../DeviceForm';
import { DeviceStockStatus, DeviceDeskAccess } from '../DeviceStatus';
import { usePermissions } from '../permissions';
import type { Site } from '../types';

export function DevicesPage({
  companyId,
  embedded = false,
}: {
  companyId?: string;
  embedded?: boolean;
}) {
  const can = usePermissions();
  const navigate = useNavigate();
  const [data, setData] = useState<DeviceListDto>();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [hosting, setHosting] = useState('');
  const [execution, setExecution] = useState('');
  const [company, setCompany] = useState(companyId ?? '');
  const [unassigned, setUnassigned] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const allowed = can('core:devices:read');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      const query = new URLSearchParams({ page: String(page), search });
      if (status) query.set('status', status);
      if (category) query.set('deviceType', category);
      if (hosting) query.set('hosting', hosting);
      if (execution) query.set('execution', execution);
      if (companyId || company) query.set('companyId', companyId ?? company);
      if (siteId) query.set('siteId', siteId);
      if (unassigned) query.set('unassigned', 'true');
      void api<DeviceListDto>(`/api/devices?${query}`, { signal: controller.signal })
        .then((result) => {
          setData(result);
          setError(undefined);
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(e);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    allowed,
    search,
    status,
    category,
    hosting,
    execution,
    company,
    companyId,
    siteId,
    page,
    unassigned,
    refresh,
  ]);
  useEffect(() => {
    setSites([]);
    const selectedCompany = companyId ?? company;
    if (!allowed || !selectedCompany) return;
    const controller = new AbortController();
    void api<Site[]>(`/api/companies/${selectedCompany}/sites`, { signal: controller.signal })
      .then(setSites)
      .catch((e) => {
        if (!controller.signal.aborted) setError(e);
      });
    return () => controller.abort();
  }, [allowed, companyId, company]);
  if (!allowed) return <Empty>Keine Berechtigung zum Lesen von Geräten.</Empty>;
  return (
    <>
      <PageHeader
        title="Geräte"
        description={
          embedded
            ? 'Geräte dieses Kunden'
            : 'Geräte finden und Kunden, Standorten und Ansprechpartnern zuordnen.'
        }
        actions={
          can('core:devices:write') && (
            <button className="button" onClick={() => setCreating(true)}>
              <Plus size={16} />
              Gerät anlegen
            </button>
          )
        }
      />
      {!embedded && can('core:devices:write') && <DeviceRegistrations />}
      {creating && (
        <Drawer
          title="Gerät anlegen"
          description="Identität und Zuordnung zentral verwalten."
          onClose={() => setCreating(false)}
        >
          <DeviceForm
            embedded
            companyId={companyId}
            onCancel={() => setCreating(false)}
            onSave={async (input) => {
              const device = await api<DeviceDto>('/api/devices', {
                method: 'POST',
                idempotencyKey: crypto.randomUUID(),
                body: JSON.stringify(input),
              });
              navigate(`/devices/${device.id}`);
            }}
          />
        </Drawer>
      )}
      <ErrorMessage error={error} />
      <div className="panel mb-4 space-y-4 p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_180px_180px]">
          <label>
            <span className="label">Geräte suchen</span>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-muted" />
              <input
                className="input w-full pl-9"
                placeholder="Name, Hostname, Seriennummer…"
                maxLength={200}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </label>
          <label>
            <span className="label">Gerätetyp</span>
            <SearchSelect
              className="input w-full"
              value={category}
              onValueChange={(value) => {
                setCategory(value);
                setPage(1);
              }}
            >
              <option value="">Alle Gerätetypen</option>
              {Object.entries(deviceTypes).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </SearchSelect>
          </label>
          <label>
            <span className="label">Status</span>
            <SearchSelect
              className="input w-full"
              value={status}
              onValueChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
            >
              <option value="">Alle Status</option>
              <option value="ACTIVE">Im Bestand</option>
              <option value="INACTIVE">Inaktiv</option>
              <option value="ARCHIVED">Archiviert</option>
            </SearchSelect>
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <label>
            <span className="label">Betriebsort</span>
            <SearchSelect
              className="input w-full"
              value={hosting}
              onValueChange={(value) => {
                setHosting(value);
                setPage(1);
              }}
            >
              <option value="">Alle Betriebsorte</option>
              {Object.entries(deviceHostings).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </SearchSelect>
          </label>
          <label>
            <span className="label">Ausführung</span>
            <SearchSelect
              className="input w-full"
              value={execution}
              onValueChange={(value) => {
                setExecution(value);
                setPage(1);
              }}
            >
              <option value="">Alle Ausführungen</option>
              {Object.entries(deviceExecutions).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </SearchSelect>
          </label>
        </div>
        {!embedded && (
          <div className="grid gap-3 md:grid-cols-3">
            <CompanyPicker
              emptyLabel="Alle Kunden"
              value={company}
              onChange={(id) => {
                setCompany(id);
                setUnassigned(false);
                setSiteId('');
                setPage(1);
              }}
            />
            <label className="self-end">
              <span className="label">Standort</span>
              <SearchSelect
                className="input w-full"
                value={siteId}
                disabled={!company}
                onValueChange={(value) => {
                  setSiteId(value);
                  setPage(1);
                }}
              >
                <option value="">Alle Standorte</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </SearchSelect>
            </label>
            <label className="flex items-center gap-2 self-end py-2 text-sm">
              <input
                type="checkbox"
                checked={unassigned}
                onChange={(e) => {
                  setUnassigned(e.target.checked);
                  setCompany('');
                  setSiteId('');
                  setPage(1);
                }}
              />
              Nur nicht zugeordnete Geräte
            </label>
          </div>
        )}
        {(search ||
          status ||
          category ||
          hosting ||
          execution ||
          (!embedded && (company || siteId || unassigned))) && (
          <button
            className="button-secondary"
            onClick={() => {
              setSearch('');
              setStatus('');
              setCategory('');
              setHosting('');
              setExecution('');
              setCompany(companyId ?? '');
              setSiteId('');
              setUnassigned(false);
              setPage(1);
            }}
          >
            <X size={14} />
            Filter zurücksetzen
          </button>
        )}
      </div>
      <div className="panel overflow-x-auto">
        {loading ? (
          <Loading>Geräte werden geladen…</Loading>
        ) : error ? (
          <Empty>
            Geräte konnten nicht geladen werden.
            <button className="button-secondary" onClick={() => setRefresh((value) => value + 1)}>
              Erneut versuchen
            </button>
          </Empty>
        ) : !data?.items.length ? (
          <Empty>Keine Geräte gefunden.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Gerät</th>
                <th>Typ / Ausführung</th>
                <th>Betriebsort</th>
                <th>Kunde / Standort</th>
                <th>Inventarnummer</th>
                <th>Bestand / DeviceDesk-Zugriff</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((device) => (
                <tr key={device.id}>
                  <td>
                    <Link
                      className="font-semibold text-accent hover:underline"
                      to={`/devices/${device.id}`}
                    >
                      {device.name}
                    </Link>
                    <div className="text-xs text-muted">{device.hostname ?? '—'}</div>
                  </td>
                  <td>
                    {deviceTypes[device.deviceType]}
                    <div className="text-xs text-muted">{deviceExecutions[device.execution]}</div>
                  </td>
                  <td>
                    {deviceHostings[device.hosting]}
                    <div className="text-xs text-muted">{device.hostingProvider}</div>
                  </td>
                  <td>
                    {device.company?.name ?? 'Nicht zugeordnet'}
                    <div className="text-xs text-muted">{device.site?.name ?? '—'}</div>
                  </td>
                  <td>{device.assetTag ?? '—'}</td>
                  <td>
                    <DeviceStockStatus status={device.status} />
                    <DeviceDeskAccess reports={device.deviceDesk} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        page={page}
        pageSize={data?.pageSize ?? 25}
        total={data?.total ?? 0}
        loading={loading || Boolean(error)}
        onPageChange={setPage}
      />
    </>
  );
}
